<template lang="pug">
    .race-clock(:class="[sizeClass, boxClass, stateClass]")
        span.race-clock__days(v-if="dayCount > 0") {{ dayCount }}d
        datetime(v-if="hasTimePart" :dt="timePart" format="HH:mm:ss" :milliseconds="1")
        span(v-else) --:--:--
    
</template>

<script>
import Datetime from '../../admin/components/datetime';
import {
  instantToEpochMilliseconds,
  instantToIso,
  nowInstant
} from '../../lib/datetime';

const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'race-clock',
  components: { Datetime },
  props: {
    start: {
      type: [String, Number],
      default: () => instantToIso(nowInstant())
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    boxed: {
      type: Boolean,
      default: true
    },
    beforeClass: {
      type: [String, Array, Object],
      default: 'text-info'
    },
    afterClass: {
      type: [String, Array, Object],
      default: 'text-warning'
    }
  },
  data() {
    return {
      started: false,
      ticker: null,
      currentTime: ''
    };
  },
  computed: {
    startTime() {
      return instantToEpochMilliseconds(this.start);
    },
    sizeClass() {
      switch (this.size) {
        case 'sm':
          return 'race-clock--sm';
        case 'md':
          return 'race-clock--md';
        default:
          return 'race-clock--lg';
      }
    },
    boxClass() {
      return this.boxed ? 'race-clock--boxed' : null;
    },
    stateClass() {
      return this.started ? this.afterClass : this.beforeClass;
    },
    dayCount() {
      if (!Number.isFinite(this.currentTime)) {
        return 0;
      }
      return Math.floor(this.currentTime / DAY_MS);
    },
    timePart() {
      if (!Number.isFinite(this.currentTime)) {
        return null;
      }
      return this.currentTime % DAY_MS;
    },
    hasTimePart() {
      return Number.isFinite(this.timePart);
    }
  },
  mounted() {
    this.updateTime();
    this.ticker = setInterval(() => this.updateTime(), 1000);
  },
  beforeDestroy() {
    clearInterval(this.ticker);
  },
  watch: {
    start() {
      this.updateTime();
    }
  },
  methods: {
    updateTime() {
      if (this.startTime == null) {
        this.started = false;
        this.currentTime = null;
        return;
      }

      const now = nowInstant().epochMilliseconds;
      this.started = now >= this.startTime;
      this.currentTime = Math.abs(now - this.startTime);
    }
  }
};
</script>

<style scoped>
.race-clock {
  font-family: monospace;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.race-clock__days {
  display: inline-block;
  margin-right: 0.35em;
  font-size: 0.72em;
  line-height: inherit;
  opacity: 0.85;
}

.race-clock--boxed {
  border: 1px solid currentColor;
  border-radius: 0.25rem;
}

.race-clock--lg {
  font-size: x-large;
  line-height: 2em;
}

.race-clock--md {
  font-size: large;
  line-height: 1.6em;
}

.race-clock--sm {
  font-size: 1rem;
  line-height: 1.3em;
}
</style>
